import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { useEditor } from '../../context/EditorContext';

const DEFAULT_MENU = [
    { id: 'home', label: 'Início', action: 'home', visible: true },
    { id: 'menu', label: 'Ementa', action: 'menu', visible: true }, 
    { id: 'wine', label: 'Carta', action: 'wine_menu', visible: true },
    { id: 'reservations', label: 'Reservas', action: 'reservations', visible: true },
    { id: 'events', label: 'Eventos', action: 'events', visible: true },
    { id: 'gallery', label: 'Galeria', action: 'gallery', visible: true },
    { id: 'about', label: 'Sobre', action: 'about', visible: true }
];

const Navbar = ({ currentPage, setCurrentPage, setView }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    
    const { theme: dbTheme } = useTheme();
    const { isEditMode, themeData: draftTheme, setSelectedElement } = useEditor();
    const theme = isEditMode ? { ...dbTheme, ...draftTheme } : dbTheme;
    const menuItems = theme.menuItems || DEFAULT_MENU;

    const { user, role, logout } = useAuth();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // LÓGICA DE TRANSPARÊNCIA ATUALIZADA
    const isHome = currentPage === 'home';
    
    // Fundo: Na Home é SEMPRE transparente. Nas outras, depende do scroll.
    const bgColor = isHome ? 'transparent' : (isScrolled ? (theme.navbarBgColor || '#FFFFFF') : 'transparent');
    
    // Cor do Texto:
    // Na Home: Se scroll, fica escuro (para ler no papel branco). Se topo, fica branco (para ler na imagem).
    // Nas outras: Segue o padrão (escuro se scroll, branco se topo).
    const textColor = isHome 
        ? (isScrolled ? '#1F2937' : '#FFFFFF') 
        : (isScrolled ? (theme.navbarTextColor || '#1F2937') : '#FFFFFF');

    // Borda:
    const borderBottom = (!isHome && isScrolled) ? '1px solid rgba(0,0,0,0.05)' : 'none';
    
    // Sombra no texto (apenas na Home quando não scrolled) para garantir leitura se houver imagens claras
    const textShadow = (isHome && !isScrolled) ? '0 2px 4px rgba(0,0,0,0.5)' : 'none';

    const navbarStyle = {
        backgroundColor: bgColor,
        color: textColor,
        transition: 'all 0.4s ease-in-out',
        padding: isScrolled ? '10px 0' : '20px 0',
        borderBottom: borderBottom,
        position: 'fixed', top: 0, left: 0, width: isEditMode ? 'calc(100% - 320px)' : '100%', zIndex: 50
    };

    const linkStyle = {
        fontFamily: '"Playfair Display", serif', fontSize: '13px', fontWeight: '700',
        textTransform: 'uppercase', letterSpacing: '2px', color: 'inherit', cursor: 'pointer',
        textShadow: textShadow // Aplica sombra se necessário
    };

    const handleEditClick = (e) => {
        if (!isEditMode) return;
        if(e.target.tagName !== 'BUTTON' && e.target.tagName !== 'A') {
            e.preventDefault(); e.stopPropagation();
            setSelectedElement({ type: 'GLOBAL', section: 'navbar', label: 'Barra de Navegação' });
        }
    };

    const handleNavClick = (pageKey) => {
        if (setCurrentPage) setCurrentPage(pageKey);
        setIsMobileMenuOpen(false);
    };

    // LOGO LOGIC: Usa o logo claro se estiver transparente ou topo da Home. Usa escuro se estiver scrolled e fundo branco.
    const useDarkLogo = isScrolled || (!isHome && isScrolled);
    const logoSrc = useDarkLogo && theme.logoEscuroUrl ? theme.logoEscuroUrl : (theme.logoClaroUrl || theme.logoEscuroUrl);

    return (
        <nav onClick={handleEditClick} style={navbarStyle} className={(!isHome && isScrolled) ? 'shadow-md' : ''}>
            <div className="container mx-auto px-6 flex justify-between items-center">
                
                {/* LOGO */}
                <div className="flex-shrink-0 cursor-pointer" onClick={() => handleNavClick('home')}>
                    {theme.logoClaroUrl ? (
                        <img 
                            src={logoSrc} 
                            alt="Logo" 
                            style={{ height: isScrolled ? '40px' : '60px', transition: 'height 0.3s' }} 
                            className="object-contain" 
                        />
                    ) : (
                        <span className="text-2xl font-bold font-serif tracking-wider" style={{ textShadow }}>Vintage</span>
                    )}
                </div>

                {/* DESKTOP MENU */}
                <div className="hidden md:flex items-center space-x-8">
                    {menuItems.map((item, idx) => (
                        item.visible && (
                            <button key={idx} onClick={() => handleNavClick(item.action)} style={linkStyle} className={`hover:text-amber-500 transition-colors ${currentPage === item.action ? 'text-amber-500' : ''}`}>
                                {item.label}
                            </button>
                        )
                    ))}

                    {user ? (
                        <div className="relative group ml-4">
                            <button style={{ textShadow }} className="text-xs font-bold uppercase tracking-widest border border-current px-3 py-1 rounded hover:bg-white hover:text-black transition-all">
                                {user.displayName || "Conta"}
                            </button>
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all text-gray-800 border-t-2 border-amber-600">
                                {(role === 'admin' || role === 'editor') && <button onClick={() => setView()} className="block w-full text-left px-4 py-2 text-xs hover:bg-gray-100 uppercase">Admin</button>}
                                <button onClick={() => handleNavClick('profile')} className="block w-full text-left px-4 py-2 text-xs hover:bg-gray-100 uppercase">Perfil</button>
                                <button onClick={logout} className="block w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 uppercase">Sair</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => handleNavClick('auth')} style={linkStyle} className="ml-4 opacity-80 hover:opacity-100 border px-4 py-2 rounded hover:bg-white hover:text-black transition-all">
                            Login
                        </button>
                    )}
                </div>

                {/* MOBILE BUTTON */}
                <div className="md:hidden">
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="focus:outline-none" style={{ textShadow }}>
                        <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
                    </button>
                </div>
            </div>

            {/* MOBILE MENU */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 bg-gray-900 z-50 flex flex-col animate-fade-in">
                    <div className="flex justify-end p-6">
                        <button onClick={() => setIsMobileMenuOpen(false)} className="text-white">
                            <i className="fas fa-times text-2xl"></i>
                        </button>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center flex-grow space-y-6">
                        {menuItems.map((item, idx) => (
                            item.visible && (
                                <button 
                                    key={idx} 
                                    onClick={() => handleNavClick(item.action)} 
                                    className="text-white text-xl font-bold uppercase tracking-widest hover:text-amber-500 transition-colors"
                                >
                                    {item.label}
                                </button>
                            )
                        ))}
                    </div>

                    <div className="p-8 border-t border-gray-800 bg-gray-900">
                        {user ? (
                            <div className="flex flex-col gap-4 text-center">
                                <span className="text-gray-400 text-sm">Logado como <strong className="text-white">{user.displayName}</strong></span>
                                {(role === 'admin' || role === 'editor') && <button onClick={() => setView()} className="text-white font-bold uppercase text-sm">Painel Admin</button>}
                                <button onClick={logout} className="text-red-500 font-bold uppercase text-sm">Sair</button>
                            </div>
                        ) : (
                            <button 
                                onClick={() => handleNavClick('auth')} 
                                className="w-full py-4 bg-amber-600 text-white font-bold uppercase tracking-widest text-sm rounded hover:bg-amber-700 transition-colors shadow-lg"
                            >
                                Entrar / Criar Conta
                            </button>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;